import { OnboardingItemKey } from "../../../common/enums";
import {
  isStoreOnlyStudio,
  studioHidesCommissions,
  studioHidesPlans,
} from "../../../common/infrastructure/helpers/studioHelper";
import { IOnboardingGroup, IOnboardingStep } from "./onboardingTypes";

// ─── Groups ───────────────────────────────────────────────────────────────────
// Shared across all flows.

export const allGroups: IOnboardingGroup[] = [
  { id: "intro", title: "Запознай се с Planny" },
  { id: "setup", title: "Настрой обекта си" },
  { id: "store", title: "Твоят магазин" },
  { id: "team", title: "Добави екип и събития" },
  { id: "golive", title: "Започни работа" },
];

// ─── Steps ────────────────────────────────────────────────────────────────────
// Single source of truth. Add new steps here only.
// To assign a step to a flow, add its id to the flow's stepIds list in flowRegistry.ts.

export const allSteps: IOnboardingStep[] = [
  // ─── Intro ───
  {
    id: OnboardingItemKey.GetToKnowPlanny,
    groupId: "intro",
    title: "Запознай се с Planny",
    description: "Научете как да навигирате в приложението.",
    route: "/users",
    sessionTrigger: true,
    targetSelector: '[data-onboarding="burger-menu"]',
    placement: "bottom",
    substeps: [
      {
        targetSelector: '[data-onboarding="burger-menu"]',
        title: "Основно меню",
        description:
          "Бургер менюто горе вляво е вашият ключ към всичко в приложението — оттук достъпвате всички секции.",
        placement: "bottom",
      },
      {
        targetSelector: '[data-onboarding="add-button"]',
        title: "Бутонът за добавяне",
        description:
          'На всяка страница горе вдясно ще намерите бутон "+". Чрез него добавяте нови записи — клиенти, класове, планове и всичко останало.',
        placement: "bottom",
      },
      {
        targetSelector: '[data-onboarding="help-button"]',
        title: "Помощ за всяка страница",
        description:
          'Бутонът "?" до бургер менюто е достъпен на всяка страница. Кликнете го по всяко време, за да видите отново обяснението за текущата страница.',
        placement: "right",
      },
    ],
  },

  // ─── Setup: Studio ───
  {
    id: OnboardingItemKey.StudioProfile,
    groupId: "setup",
    title: "Редактирайте обекта си",
    description:
      "Отворете настройките на обекта, за да конфигурирате основната информация, плащанията и напомнянията.",
    route: "/studios",
    targetSelector: 'div[class*="container_items_item"]:first-child',
    placement: "bottom",
    substeps: [
      {
        targetSelector: 'div[class*="container_items_item"]:first-child',
        title: "Вашите обекти",
        description:
          "Тук се намират всички ваши обекти. За да редактирате обект, натиснете бутона за редакция. Ако не го виждате, плъзнете наляво върху реда, за да го разкриете.",
        placement: "bottom",
      },
    ],
  },
  {
    id: OnboardingItemKey.StudioWizard,
    groupId: "setup",
    title: "Настройки на обект",
    description:
      'Всяка настройка тук контролира работните процеси и бизнес логиката на обекта. Прегледайте внимателно всички секции. Най-важните са "Основна информация" и "Плащания" \u2014 уверете се, че са конфигурирани правилно. От секция "Напомняния" може да настроите автоматични известия към клиентите си.',
    matchRoutes: ["/studios/edit"],
    hidden: true,
  },
  {
    id: OnboardingItemKey.AddBillingData,
    groupId: "setup",
    title: "Добавете данни за фактуриране",
    description:
      "Въведете данните на фирмата си, за да можем да издадем фактура когато тестовият период приключи.",
    route: "/my-profile",
    matchRoutes: ["/my-profile"],
    substeps: [
      {
        title: "Данни за фактуриране",
        description:
          "Моля въведете данните на фирмата си, за да можем да издадем фактура когато тестовият период приключи.",
      },
    ],
  },

  // ─── Setup: Nomenclatures ───
  {
    id: OnboardingItemKey.PlanTypes,
    groupId: "setup",
    title: "Добавете типове планове",
    description:
      "Тук можете да откриете всички типове планове, които обекта ви предлага.",
    route: "/plan-types",
    targetSelector: '[data-onboarding="nav-plan-types"]',
    placement: "right",
    condition: (ctx) =>
      ctx.studio.studioUsesDifferentPlanTypes && !studioHidesPlans(ctx.studio),
    substeps: [
      {
        targetSelector: '[data-onboarding="nav-plan-types"]',
        title: "Добавете типове планове",
        description:
          "Тук можете да откриете всички типове планове, които обекта ви предлага. Например, ако предлагате отделни абонаменти за масажи и отделни за пилатес реформър — това са два различни типа плана.",
        placement: "right",
        optional: false,
      },
      {
        title: "Как работят типовете?",
        description:
          "Създавате тип план \u201eМасаж\u201c и тип план \u201eПилатес реформър\u201c. След това от Бургер меню \u2192 Администрация \u2192 Планове създавате конкретните абонаментни планове, които принадлежат към съответния тип.",
      },
      {
        title: "Връзка с плановете",
        description:
          "Когато добавяте нов план, ще можете да изберете от кой тип е. Един тип може да съдържа повече от един план \u2014 например тип \u201eМасаж\u201c с планове \u201eМасаж 4 посещения\u201c и \u201eМасаж 8 посещения\u201c.",
      },
      {
        title: "Връзка с груповите класове",
        description:
          "Когато добавяте групов клас, ще имате опция да изберете с кои типове абонаментни карти може да бъде платен — така се избягват обърквания при отбелязване на посещения.",
      },
    ],
  },
  {
    id: OnboardingItemKey.HallTypes,
    groupId: "setup",
    title: "Добавете всички зали на обекта",
    description:
      "Тук можете да откриете и управлявате всички зали/помещения на обекта.",
    route: "/halls",
    targetSelector: '[data-onboarding="nav-halls"]',
    placement: "right",
    condition: (ctx) => ctx.studio.tenantUsesMoreThanOneHall,
  },
  {
    id: OnboardingItemKey.ServiceTypes,
    groupId: "setup",
    title: "Добавете услугите, които предлагате",
    description:
      "В тази страница се намират всички услуги, които предлагате, разделени по категории.",
    route: "/service-type",
    targetSelector: '[data-onboarding="nav-service-types"]',
    placement: "right",
    condition: (ctx) => ctx.showServicesTypeOptions,
    substeps: [
      {
        targetSelector: '[data-onboarding="nav-service-types"]',
        title: "Добавете услугите, които предлагате",
        description:
          "В тази страница се намират всички услуги, които предлагате. Разделени са в категории.",
        placement: "right",
        optional: false,
      },
      {
        targetSelector: '[data-onboarding="add-button"]',
        title: "Добавяне на категория",
        description: "Можете да добавите категория от тук.",
        placement: "bottom",
      },
      {
        targetSelector: '[data-onboarding="uncategorized-row"]',
        title: "Без категория",
        description:
          "Всяка услуга без категория отива в тази категория. Потребителите я виждат като \u201eДруги\u201c.",
        placement: "bottom",
      },
      {
        targetSelector: '[data-onboarding="uncategorized-menu"]',
        title: "Управление на услугите",
        description:
          "От това меню можете да добавите услуга или да подредите услугите в дадена категория по ваш вкус. Както ги подредите, така ще излизат и на потребителите.",
        placement: "bottom",
      },
      {
        title: "Подреждане на категориите",
        description:
          "Когато добавите повече от една категория (освен \u201eБез категория\u201c), в горната част на екрана ще се появи бутон за подреждане на категориите. Както ги подредите \u2014 така ще се визуализират на потребителите.",
      },
    ],
  },
  {
    id: OnboardingItemKey.ClassCategories,
    groupId: "setup",
    title: "Категории за класове",
    description:
      "Организирайте груповите класове по категории, за да могат потребителите лесно да ги намират.",
    route: "/event-type-categories",
    targetSelector: '[data-onboarding="nav-class-categories"]',
    placement: "right",
    condition: (ctx) => ctx.studio.useGroupClassCategories,
    substeps: [
      {
        targetSelector: '[data-onboarding="nav-class-categories"]',
        title: "Категории за класове",
        description:
          "Тъй като сте активирали опцията за категории в настройките на обекта, тази страница ви позволява да ги управлявате.",
        placement: "right",
        optional: false,
      },
      {
        targetSelector: '[data-onboarding="add-button"]',
        title: "Добавете категории",
        description:
          "Оттук можете да добавяте различни категории за класове \u2014 например \u201eСпининг\u201c, \u201eЙога\u201c, \u201eМодерни танци\u201c и други.",
        placement: "bottom",
      },
      {
        title: "Как се използват?",
        description:
          "При създаване на клас ще можете да изберете към коя категория спада. По този начин потребителите ще виждат всички ваши класове подредени и категоризирани.",
      },
    ],
  },
  {
    id: OnboardingItemKey.CreatePlan,
    groupId: "setup",
    title: "Създайте първия си план",
    description:
      "Създайте абонаментен план, който клиентите ще могат да закупят за достъп до вашите услуги.",
    route: "/plans",
    targetSelector: '[data-onboarding="nav-plans"]',
    placement: "right",
    // Subscription plans are irrelevant for a shop-only studio and for a studio that
    // switched them off in its settings.
    condition: (ctx) =>
      !isStoreOnlyStudio(ctx.studio) && !studioHidesPlans(ctx.studio),
    substeps: [
      {
        targetSelector: '[data-onboarding="nav-plans"]',
        title: "Абонаментни планове",
        description:
          "Тук се намират всички ваши абонаментни планове. Клиентите закупуват план, за да получат преференциален достъп до вашите услуги.",
        placement: "right",
        optional: false,
      },
      {
        targetSelector: '[data-onboarding="add-button"]',
        title: "Добавете първия си план",
        description: "Натиснете бутона, за да създадете нов абонаментен план.",
        placement: "bottom",
        optional: true,
      },
    ],
  },
  {
    id: OnboardingItemKey.PlanWizard,
    groupId: "setup",
    title: "Създайте абонаментен план",
    description:
      'Планът е шаблон, който се използва при зареждане на карта на клиент. Полето "Целева група" определя на кого може да се продаде: изберете "Потребители" за индивидуални клиенти, или "Семейства" за семейни абонаменти. За повече информация относно семействата отидете на Бургер меню \u2192 Администрация \u2192 Семейства.',
    matchRoutes: ["/plan/add"],
    hidden: true,
    condition: (ctx) => !studioHidesPlans(ctx.studio),
  },

  // ─── Team: Employees ───
  {
    id: OnboardingItemKey.EmployeesList,
    groupId: "team",
    title: "Запознайте се със служителите",
    description:
      "Разгледайте списъка с всички служители и научете как да добавяте нови.",
    route: "/employees",
    targetSelector: 'div[class*="container_items_item"]:first-child',
    placement: "bottom",
    optional: true,
    substeps: [
      {
        targetSelector: 'div[class*="container_items_item"]:first-child',
        title: "Вашите служители",
        description:
          "Тук живеят всички служители на обекта. Можете да ги редактирате и изтривате от тук.",
        placement: "bottom",
        optional: false,
      },
      {
        targetSelector: '[data-onboarding="add-button"]',
        title: "Добавете нов служител",
        description: "Оттук можете да добавите нов служител.",
        placement: "bottom",
        optional: true,
      },
    ],
  },
  {
    id: OnboardingItemKey.EmployeeWizard,
    groupId: "team",
    title: "Добавете служител",
    description:
      'Попълнете данните за новия служител и натиснете "Добавяне". Моля, проверете внимателно телефона и имейла \u2014 ако не са коректни, служителят няма да може да влезе в системата. Веднага след като го добавите, той ще може да влезе с тези данни.',
    route: "/employee/add",
    optional: true,
    hidden: true,
  },

  // ─── Team: Events ───
  {
    id: OnboardingItemKey.CreateEvent,
    groupId: "team",
    title: "Създайте първото си събитие",
    description: "Създайте първото си събитие в календара.",
    route: "/calendar",
    targetSelector: '[data-onboarding="add-button"]',
    placement: "bottom",
    // A shop-only studio has no calendar events.
    condition: (ctx) => !isStoreOnlyStudio(ctx.studio),
  },
  {
    id: OnboardingItemKey.EventWizard,
    groupId: "team",
    title: "Попълнете данните за събитието",
    description: "Попълнете задължителните полета за събитието.",
    matchRoutes: ["/planner/add"],
    hidden: true,
    condition: (ctx) =>
      ctx.showGroupTypeOptions && !ctx.showServicesTypeOptions,
    substeps: [
      {
        title: "Попълнете данните",
        description:
          "Попълнете задължителните полета за събитието. Цената по подразбиране и наличните места се контролират от настройките на обекта.",
      },
      {
        targetSelector: '[data-onboarding="event-wizard-extra-settings"]',
        title: "Допълнителни настройки",
        description:
          "Тук можете да включите или изключите допълнителни опции — видимост в портала, онлайн резервации, предплащане и други.",
        placement: "top",
      },
    ],
  },
  {
    id: OnboardingItemKey.EventWizardHybrid,
    groupId: "team",
    title: "Попълнете данните за събитието",
    description: "Попълнете задължителните полета за събитието.",
    matchRoutes: ["/planner/add"],
    hidden: true,
    condition: (ctx) => ctx.showGroupTypeOptions && ctx.showServicesTypeOptions,
    substeps: [
      {
        targetSelector: '[data-onboarding="event-wizard-tabs"]',
        title: "Избор на тип събитие",
        description:
          "От тези табове избирате какъв тип събитие искате да създадете \u2014 групов клас, индивидуална услуга или блокирано време.",
        placement: "bottom",
      },
      {
        title: "Попълнете данните",
        description:
          "Попълнете задължителните полета за събитието. Цената по подразбиране и наличните места се контролират от настройките на обекта.",
      },
      {
        targetSelector: '[data-onboarding="event-wizard-extra-settings"]',
        title: "Допълнителни настройки",
        description:
          "Тук можете да включите или изключите допълнителни опции \u2014 видимост в портала, онлайн резервации, предплащане и други.",
        placement: "top",
      },
    ],
  },

  {
    id: OnboardingItemKey.ServiceWizardGuide,
    groupId: "team",
    title: "Създаване на услуга",
    description: "Научете как работят резервациите за услуги.",
    hidden: true,
    condition: (ctx) => ctx.showServicesTypeOptions,
    substeps: [
      {
        targetSelector: '[data-onboarding="event-wizard-service-tab"]',
        title: "Резервация за услуга",
        description:
          "От този таб създавате резервация за индивидуална услуга. При нея може да присъства само един клиент \u2014 подходящо за масаж, подстригване и подобни.",
        placement: "bottom",
      },
      {
        title: "Откъде идват услугите?",
        description:
          "Списъкът с услуги се управлява от Бургер меню \u2192 Администрация \u2192 Други \u2192 Типове услуги. Оттам добавяте, редактирате и организирате всички услуги, които предлагате.",
      },
      {
        title: "Кой изпълнява услугата?",
        description:
          "Всяка услуга може да бъде изпълнявана от определени служители. При редактиране на услугата можете да изберете кои служители имат право да я извършват.",
      },
      {
        title: "Свободни часове",
        description:
          "Всеки служител има собствено работно време. На базата на него и заетите часове, в календара се показват само свободните слотове за резервация.",
      },
      {
        title: "Работен график",
        description:
          "Работният график на служител се променя от Бургер меню \u2192 Администрация \u2192 Служители \u2192 редактирайте служителя \u2192 бутон \u201eРаботен график\u201c в долната част на страницата.",
      },
    ],
  },
  {
    id: OnboardingItemKey.BlockedTimeGuide,
    groupId: "team",
    title: "Блокирано време",
    description: "Научете как да блокирате часове в календара.",
    hidden: true,
    condition: (ctx) => ctx.showServicesTypeOptions,
    substeps: [
      {
        targetSelector: '[data-onboarding="event-wizard-blocked-tab"]',
        title: "Блокирано време",
        description:
          "Блокираното време е слот, в който потребителите не могат да правят резервации. Използвайте го, когато служител е в обедна почивка, извън обекта или по друга причина не е на разположение.",
        placement: "bottom",
      },
      {
        title: "Типове блокирания",
        description:
          "Типовете блокирания се управляват от Бургер меню \u2192 Администрация \u2192 Други \u2192 Типове блокирания. Ако не виждате нито един тип блокиране в момента, трябва да добавите такъв от администрацията.",
      },
      {
        title: "Как работят типовете?",
        description:
          "Типът блокиране служи като шаблон \u2014 например \u201eОбяд \u2014 30 мин.\u201c. Когато добавяте блокирано време, продължителността може да се промени, но трябва да имате поне един тип, добавен в администрацията.",
      },
    ],
  },

  // ─── Go Live: Users ───
  {
    id: OnboardingItemKey.FirstUser,
    groupId: "golive",
    title: "Добавете първия си потребител",
    description:
      "Тук се намират всички потребители в обекта. Нека добавим първия.",
    route: "/users",
    substeps: [
      {
        title: "Вашите потребители",
        description:
          "Тук са всички хора, регистрирани в обекта ви. Кликнете на потребител, за да видите пълния му профил.",
        placement: "bottom",
      },
      {
        targetSelector: '[data-onboarding="add-button"]',
        title: "Добавете нов потребител",
        description:
          'Натиснете "+" горе вдясно, за да регистрирате нов потребител.',
        placement: "bottom",
        optional: true,
      },
      {
        title: "Тестов потребител",
        description:
          "В списъка ще откриете тестов потребител (най-често с телефонен номер, започващ с +359123123ХХХ). Можете да го използвате, за да влезете в потребителския портал и да видите как изглежда от гледната точка на клиент. При първото влизане системата ще ви помоли да зададете парола за този акаунт.",
        // The user portal preview is irrelevant for a shop-only studio.
        condition: (ctx) => !isStoreOnlyStudio(ctx.studio),
      },
    ],
  },
  {
    id: OnboardingItemKey.UserAdd,
    groupId: "golive",
    title: "Добавяне на потребител",
    description:
      "Телефонният номер е най-важното поле при добавяне на потребител.",
    route: "/user/add",
    hidden: true,
    substeps: [
      {
        title: "Телефонният номер е ключов",
        description:
          "Можете да създадете потребител без телефонен номер, но тогава той не може да достъпи своя профил. Препоръчваме ВИНАГИ да въвеждате телефон — така се избягват грешки и объркване. За начало, имената и телефонният номер са напълно достатъчни.",
      },
    ],
  },
  {
    id: OnboardingItemKey.UserProfile,
    groupId: "golive",
    title: "Профил на потребителя",
    description: "Запознайте се с профила на потребителя.",
    matchRoutes: ["/user/"],
    hidden: true,
    // The user profile tour is built around plans/reservations, none of which a
    // shop-only studio uses, so skip the whole tour there.
    condition: (ctx) => !isStoreOnlyStudio(ctx.studio),
    substeps: [
      {
        targetSelector: '[data-onboarding="user-phone"]',
        title: "Телефон и Viber",
        description:
          "На мобилно устройство, при кликване на телефонния номер можете директно да наберете потребителя. Viber бутонът отваря Viber профила им — удобно за бързо съобщение.",
        placement: "bottom",
      },
      {
        targetSelector: '[data-onboarding="user-plans"]',
        title: "Планове",
        description:
          "Тук можете да видите всички абонаментни планове (карти), закупени от потребителя.",
        placement: "bottom",
        // The row is not on the profile when the studio does not use plans.
        condition: (ctx) => !studioHidesPlans(ctx.studio),
      },
      {
        targetSelector: '[data-onboarding="user-additional-details"]',
        title: "Допълнителни детайли",
        description:
          "Тук ще намерите доста допълнителна информация: статистики за посещения, пропуснати и предстоящи часове, общ приход, дадени съгласия и потвърждение на имейл. Само собственикът и мениджърите виждат статистиките. Ако потребителят има проблем с потвърждаването на имейла, можете да го потвърдите ръчно от тук.",
        placement: "bottom",
      },
      {
        targetSelector: '[data-onboarding="user-notes"]',
        title: "Бележки",
        description:
          "Можете да добавяте, редактирате и изтривате бележки за конкретния потребител — видими само за персонала.",
        placement: "bottom",
      },
      {
        targetSelector: '[data-onboarding="user-generate-link"]',
        title: "Генериране на линк за парола",
        description:
          "Ако поради някаква причина потребителят не е получил имейл за възстановяване на парола, администраторът може да генерира линк за възстановяване на паролата и да му го изпрати ръчно.",
        placement: "bottom",
      },
      {
        targetSelector: '[data-onboarding="user-unreliable"]',
        title: "Маркиране като нередовен",
        description:
          "Много важна функция. Веднъж маркиран като нередовен, потребителят не може да прави резервации сам. Навсякъде в системата ще бъде отбелязан като нередовен за персонала. Потребителят не вижда това — просто не може да резервира.",
        placement: "bottom",
      },
      {
        targetSelector: '[data-onboarding="user-more-actions"]',
        title: "Още действия",
        description:
          "В това меню са историята на профила (пълна хронология от създаването му), сливането с друг акаунт (когато клиентът има два профила, всичко от единия се прехвърля в другия) и перманентното изтриване на резервации, което виждат само мениджърите и собственикът.",
        placement: "bottom",
      },
    ],
  },

  // ─── Go Live: Dashboard & Reporting ───
  {
    id: OnboardingItemKey.DashboardKpis,
    groupId: "golive",
    title: "Запознайте се с таблото",
    description:
      "Тук виждате ключовите показатели за дейността - приходи, нови потребители, присъствия и други.",
    route: "/dashboard",
  },
  {
    id: OnboardingItemKey.Statistics,
    groupId: "golive",
    title: "Репорти",
    description:
      "Детайлни отчети за приходи, планове, присъствия и други аспекти на вашия бизнес.",
    route: "/statistics",
    targetSelector: '[data-onboarding="nav-statistics"]',
    placement: "right",
    substeps: [
      {
        targetSelector: '[data-onboarding="nav-statistics"]',
        title: "Репорти",
        description:
          "Детайлни отчети за приходи, планове, присъствия и други аспекти на вашия бизнес.",
        placement: "right",
        optional: false,
      },
      {
        targetSelector: '[data-onboarding="stats-employee-filter"]',
        title: "Филтър по служител",
        description:
          "Собственикът и мениджърите на обекта имат достъп до репортите на всички служители. Другите роли виждат само собственото си име и данни.",
        placement: "top",
        optional: true,
      },
    ],
  },
  {
    id: OnboardingItemKey.StatisticsDetail,
    groupId: "golive",
    title: "Детайли на репорта",
    description: "Разгледайте всички метрики и научете как да ги четете.",
    matchRoutes: ["/statistics/"],
    hidden: true,
    substeps: [
      {
        title: "Детайли на репорта",
        description:
          "На тази страница са показани и изчислени всички метрики за избрания период. Всяка стойност, която е подчертана, може да се кликне \u2014 ще се отвори диалог с детайли кои записи участват в изчислението на тази метрика.",
      },
      {
        targetSelector: '[data-onboarding="stats-export-btn"]',
        title: "Експорт в Excel",
        description:
          "С този бутон можете да експортирате всичко, което виждате на страницата, в Excel файл.",
        placement: "bottom",
        optional: true,
      },
    ],
  },
  {
    id: OnboardingItemKey.Support,
    groupId: "golive",
    title: "Свържете се с поддръжка",
    description:
      "Ако имате въпроси или нужда от помощ, екипът на Planny е на ваше разположение. Можете да откриете тази страница от Бургер менюто → Поддръжка.",
    route: "/support",
    targetSelector: '[data-onboarding="nav-support"]',
    placement: "right",
  },

  // ─── Page Guides (hidden, component-driven) ───
  {
    id: OnboardingItemKey.RefillCardGuide,
    groupId: "golive",
    title: "Презареждане на карта",
    description:
      "Научете как да заредите абонаментна карта на потребител или семейство.",
    hidden: true,
    condition: (ctx) => !studioHidesPlans(ctx.studio),
    substeps: [
      {
        title: "Какво е презареждане?",
        description:
          "На тази страница добавяте абонаментен план (карта) към потребител или семейство. Изберете план, потребител, период и цена.",
      },
      {
        title: "Цена на посещение",
        description:
          "Ако планът е за 12 посещения на обща цена 120 евро, в репортите ще бъде отчетен като 10 евро на посещение. Цената на посещение се изчислява автоматично.",
      },
      {
        targetSelector: '[data-onboarding="sold-by-field"]',
        title: "Продадена от",
        description:
          'Важно е кой служител продава плана. В репорта от тип "Пари" комисионната печалба ще се изчисли на служителя, избран в това поле.',
        placement: "top",
        // The step only explains the seller's commission.
        condition: (ctx) => !studioHidesCommissions(ctx.studio),
      },
      {
        title: "Редактиране на продаден план",
        description:
          "След като планът бъде продаден, можете да го редактирате от профила на потребителя → секция Планове → кликнете върху плана → бутон за редактиране горе вдясно.",
      },
    ],
  },
  {
    id: OnboardingItemKey.RefillCardEmpty,
    groupId: "golive",
    title: "Презареждане на карта",
    description: "Тази страница изисква поне един план и един потребител.",
    hidden: true,
    condition: (ctx) => !studioHidesPlans(ctx.studio),
    substeps: [
      {
        title: "Необходими настройки",
        description:
          "За да работи тази страница, обекта трябва да има поне 1 абонаментен план и поне 1 потребител. Можете да ги добавите от Бургер меню → Администрация → Планове / Потребители.",
      },
    ],
  },
  {
    id: OnboardingItemKey.InventoryGuide,
    groupId: "store",
    title: "Продукти",
    description:
      "Научете как да управлявате продуктите, които обекта ви продава.",
    route: "/inventory/products",
    // Page guide for every studio; surfaced as a setup task for store-only studios.
    hidden: (ctx) => !isStoreOnlyStudio(ctx.studio),
    substeps: [
      {
        title: "Продукти на обекта",
        description:
          "В тази страница се намират всички продукти, които обекта ви продава. Те са групирани по категории.",
      },
      {
        targetSelector: '[data-onboarding="add-button"]',
        title: "Добавяне на категория",
        description:
          "Оттук можете да добавите нова категория. Всяка категория трябва да има емоджи, за да бъде по-лесно разпознаваема из платформата.",
        placement: "bottom",
      },
      {
        targetSelector: '[data-onboarding="uncategorized-row"]',
        title: "Без категория",
        description:
          "Продуктите, които нямат присвоена категория, отиват в тази категория.",
        placement: "bottom",
      },
      {
        targetSelector: '[data-onboarding="uncategorized-menu"]',
        title: "Управление на продуктите",
        description:
          "От това меню можете да добавите нови продукти към категорията.",
        placement: "bottom",
      },
      {
        title: "Подреждане на продуктите",
        description:
          "Продуктите в рамките на категория могат да бъдат пренаредени по ваш вкус.",
      },
      {
        title: "Подреждане на категориите",
        description:
          "Когато имате повече от една категория (освен \u201eБез категория\u201c), в горната част на екрана ще се появи бутон за подреждане на категориите.",
      },
      {
        title: "Баркодове",
        description:
          "Препоръчваме всеки продукт да има сканиран баркод. Ако нямате баркод скенер, можете да използвате камерата на устройството си. Нужно е да дадете разрешение на платформата да използва камерата за тази цел.",
      },
    ],
  },
  {
    id: OnboardingItemKey.InventoryTransfersGuide,
    groupId: "store",
    title: "Доставки",
    description:
      "Научете как да планирате и управлявате доставките на продукти.",
    route: "/inventory/transfers",
    // Page guide for every studio; surfaced as a setup task for store-only studios.
    hidden: (ctx) => !isStoreOnlyStudio(ctx.studio),
    substeps: [
      {
        title: "Планиране на доставки",
        description:
          "На тази страница можете да планирате всички доставки на продукти за обекта.",
      },
      {
        title: "Как работи?",
        description:
          "Например, ако утре очаквате доставка от 100 бутилки вода, 20 постелки за йога и 130 протеинови барчета — създайте планирана доставка за утре и добавете всички тези продукти към нея.",
      },
      {
        title: "Приключване на доставка",
        description:
          "Когато доставката пристигне, можете да я редактирате и да смените статуса на 'завършена', при което наличните количества на продуктите ще бъдат автоматично актуализирани.",
      },
    ],
  },
  {
    id: OnboardingItemKey.CashdeskGuide,
    groupId: "store",
    title: "Каса",
    description: "Научете как да използвате касата за продажба на продукти.",
    // No route: the cashdesk page itself decides between the empty-state guide and
    // the real tour, so the checklist navigates here on click (see OnboardingSetupGuide)
    // rather than letting the route orchestrator activate this step.
    // Page guide for every studio; surfaced as a setup task for store-only studios.
    hidden: (ctx) => !isStoreOnlyStudio(ctx.studio),
    substeps: [
      {
        targetSelector: '[data-onboarding="cashdesk-search"]',
        title: "Търсене на продукти",
        description:
          "От това поле можете да търсите продукти по име, марка, баркод или SKU.",
        placement: "bottom",
      },
      {
        targetSelector: '[data-onboarding="cashdesk-barcode-btn"]',
        title: "Баркод скенер",
        description:
          "Ако нямате свързан баркод четец, можете да използвате камерата на устройството си за сканиране. По-бързо и удобно е от ръчно въвеждане.",
        placement: "bottom",
      },
      {
        targetSelector: '[data-onboarding="cashdesk-first-product"]',
        title: "Добавяне на продукт",
        description:
          "При кликване върху продукт, той автоматично се добавя в количката.",
        placement: "bottom",
      },
      {
        targetSelector: '[data-onboarding="cashdesk-cart"]',
        title: "Количка",
        description:
          "Тук можете да променяте количествата на добавените продукти. Когато поръчката е готова, натиснете бутона за плащане.",
        placement: "left",
      },
      {
        targetSelector: '[data-onboarding="cashdesk-orders-tab"]',
        title: "Поръчки",
        description: "В този таб се намират всички предишни поръчки.",
        placement: "bottom",
      },
      {
        title: "Изтриване на поръчки",
        description:
          "Вече създадените поръчки не могат да бъдат редактирани. Могат да бъдат изтрити само от собственика или мениджър на обекта.",
      },
    ],
  },
  {
    id: OnboardingItemKey.CashdeskEmpty,
    groupId: "golive",
    title: "Каса",
    description: "Касата изисква добавени продукти.",
    hidden: true,
    substeps: [
      {
        title: "Каса",
        description:
          "От касата можете да продавате продукти от инвентара на обекта на потребители от системата или анонимни клиенти.",
      },
      {
        title: "Необходими настройки",
        description:
          "За да използвате касата, трябва първо да добавите продукти. Отидете на Бургер меню \u2192 Инвентар \u2192 Продукти и добавете поне един продукт.",
      },
    ],
  },
  {
    id: OnboardingItemKey.FamiliesGuide,
    groupId: "golive",
    title: "Семейства",
    description: "Научете как работят семействата в платформата.",
    hidden: true,
    substeps: [
      {
        title: "Какво е семейство?",
        description:
          "Тук се намират всички семейства. Семейството е група от потребители, които са свързани помежду си.",
      },
      {
        title: "Отговорник на семейството",
        description:
          "Всяко семейство може да има отговорник. Той може да прави резервации от името на останалите членове на семейството.",
      },
      {
        title: "Семеен абонаментен план",
        description:
          "Семействата могат да имат общ абонаментен план, който е с целева група \u201eСемейства\u201c.",
      },
      {
        title: "Създаване на семейство от портала",
        description:
          "Потребителите могат да създадат собствено семейство от потребителския портал, но могат да добавят само потребители, които все още не са регистрирани в платформата. Ако потребител вече е в системата, администратор на обекта трябва да отиде в новосъздаденото семейство и да добави ръчно съществуващия потребител.",
      },
    ],
  },
  {
    id: OnboardingItemKey.InventoryOverviewGuide,
    groupId: "store",
    title: "Наличности",
    description: "Научете как работи страницата за наличности.",
    route: "/inventory/overview",
    // Page guide for every studio; surfaced as a setup task for store-only studios.
    hidden: (ctx) => !isStoreOnlyStudio(ctx.studio),
    substeps: [
      {
        title: "Обобщение на наличностите",
        description:
          "На тази страница ще откриете обобщение за продуктите на обекта — кои ще изтекат скоро и кои са с ниска наличност. Целта е да видите с един поглед на какво трябва да обърнете внимание.",
      },
      {
        targetSelector: '[data-onboarding="inventory-overview-expiration-tab"]',
        title: "По срок на годност",
        description:
          "В този таб се показват всички продукти, чийто срок на годност изтича до 10 дни или вече е изтекъл. Реално, това са партиди от доставки, а не отделни продукти — една и съща стока може да се повтори, ако е дошла с различни доставки с различни срокове.",
        placement: "bottom",
      },
      {
        targetSelector: '[data-onboarding="inventory-overview-expiration-tab"]',
        title: "Унищожаване на изтекли продукти",
        description:
          "Собственикът и мениджърите на обекта могат да унищожат вече изтеклите продукти директно от тази страница. При натискане на бутона „Унищожи“, количеството на продукта автоматично намалява с количеството на изтеклата партида, а действието се записва в историята му.",
        placement: "bottom",
      },
      {
        targetSelector: '[data-onboarding="inventory-overview-stock-tab"]',
        title: "По наличност",
        description:
          "Тук виждате всички продукти на обекта, подредени по най-ниска наличност. Продукти, при които прагът за ниска наличност е настроен на 0, не се показват никъде в тази страница — приема се, че не ви интересуват. Всички останали са тук, готови за бърз преглед.",
        placement: "bottom",
      },
      {
        title: "Ежедневна справка по имейл",
        description:
          "Всеки ден системата автоматично изпраща справка на имейла на обекта ({studioEmail}), ако има продукти, които изтичат скоро или са с ниска наличност.",
      },
    ],
  },
  {
    id: OnboardingItemKey.GymVisitsGuide,
    groupId: "golive",
    title: "Управление на посещения в зала",
    description: "Научете как се отбелязват посещения, плащания и история.",
    route: "/gym",
    hidden: true,
    condition: (ctx) => ctx.showGymTypeOptions,
    substeps: [
      {
        title: "Управление на посещенията в залата",
        description:
          "От тази страница управлявате всички посещения в залата \u2014 отбелязвате влизане/излизане, проверявате кой е вътре в момента и преглеждате пълната история.",
      },
      {
        targetSelector: '[data-onboarding="gym-search"]',
        title: "Търсене на потребител",
        description:
          "Всеки потребител има профил с имена, телефон, имейл и RFID код. Кодът отговаря на гривна или карта, която клиентът сканира, ако залата има скенер. За да отбележете посещение, намерете потребителя от това поле \u2014 по име, телефон, имейл или чрез сканиране на картата.",
        placement: "bottom",
      },
      {
        targetSelector: '[data-onboarding="gym-add-user"]',
        title: "Бързо добавяне на потребител",
        description:
          "Ако клиентът все още няма профил в системата, с този бутон бързо създавате нов потребител, без да напускате тази страница.",
        placement: "bottom",
      },
      {
        targetSelector: '[data-onboarding="gym-profile-placeholder"]',
        title: "Профил на избрания потребител",
        description:
          "Когато изберете потребител, тук се показва неговият профил с действията \u2014 отбелязване на влизане/излизане, отмяна на последно сканиране, отваряне на каса и редакция на профил.",
        placement: "right",
      },
      {
        targetSelector: '[data-onboarding="gym-inside-header"]',
        title: "Трениращи в момента",
        description:
          "Тук са изброени всички потребители, които в момента са в залата. Кликнете върху някого, за да го отворите и да го отбележете за излизане ръчно \u2014 например, когато не е сканирал картата си на излизане.",
        placement: "bottom",
      },
      {
        title: "История на посещенията",
        description:
          "По-долу на страницата ще намерите пълната хронология на всички посещения в залата. Таблицата е само за справка \u2014 записите не могат да се редактират.",
      },
      {
        title: "Как се плащат посещенията?",
        description:
          "Когато потребител има активен абонаментен план, посещението автоматично се отчита към плана. Ако няма активен план, системата автоматично отваря диалог, в който избирате как да бъде платено посещението.",
      },
    ],
  },

  {
    id: OnboardingItemKey.BreakTypesGuide,
    groupId: "team",
    title: "Типове блокирания",
    description: "Научете как работят типовете блокирания.",
    hidden: true,
    condition: (ctx) => ctx.showServicesTypeOptions,
    substeps: [
      {
        title: "Какво е тип блокиране?",
        description:
          "Типът блокиране служи като шаблон за блокирано време в календара. Блокираното време е слот, в който потребителите не могат да правят резервации.",
      },
      {
        title: "Как се използва?",
        description:
          "Например, можете да създадете тип \u201eОбяд \u2014 30 мин.\u201c. Когато добавяте блокирано време в календара, избирате този тип и продължителността се попълва автоматично, но може да бъде променена.",
      },
      {
        title: "Кога да блокирате?",
        description:
          "Използвайте блокирано време, когато служител е в обедна почивка, извън обекта или по друга причина не е на разположение за резервации.",
      },
    ],
  },
];

// ─── Step lookup map ───────────────────────────────────────────────────────────
const stepMap = new Map(allSteps.map((s) => [s.id, s]));

/**
 * Build a flow from a list of step IDs.
 * Steps are returned in the order of the provided IDs.
 * Unknown IDs are silently skipped.
 */
export function buildFlow(stepIds: OnboardingItemKey[]): {
  groups: IOnboardingGroup[];
  steps: IOnboardingStep[];
} {
  const steps = stepIds.flatMap((id) => {
    const step = stepMap.get(id);
    return step ? [step] : [];
  });
  const usedGroupIds = new Set(steps.map((s) => s.groupId));
  const groups = allGroups.filter((g) => usedGroupIds.has(g.id));
  return { groups, steps };
}
